/**
 * The GraphQL counterpart to the REST {@link api} wrapper (#2096, Tier 1).
 *
 * Collapses the ~164 copy-pasted post-query blocks of the form
 *
 * ```ts
 * const { data, errors } = await apolloClient.query({ ... });
 * if (errors?.length) throw new Error(errors[0].message);
 * return data.someField; // unguarded — `data` could be null
 * ```
 *
 * into a single call that asserts there are no GraphQL `errors`, asserts `data`
 * is present, and otherwise throws a normalized {@link ApiError} (so a GraphQL
 * failure routed through {@link handleApiError} gets the same toast + request-id
 * treatment as a REST failure). The returned `data` is non-nullable, removing
 * the unguarded `data.field` access that made the copy-pasted blocks unsafe.
 *
 * @example
 * const data = runGraphQL(
 *   await apolloClient.query({ query: GetClubPathwayChartDocument, variables }),
 *   { fallbackMessage: "Failed to load pathway chart" },
 * );
 * return data.clubPathwayChart ?? [];
 */

import { ApiError, readErrorCode } from "@/core/api";

/**
 * The shape of an Apollo `FetchResult` / `ApolloQueryResult` we depend on — kept
 * structural (and `data` deliberately `unknown`) so the whole concrete result
 * type is inferred as `R` at the call site. `NonNullable<R["data"]>` then yields
 * the generated query type without any explicit type argument.
 */
export interface GraphQLResultLike {
  data?: unknown;
  errors?: readonly { message: string; extensions?: Record<string, unknown> | null }[] | null;
}

export interface RunGraphQLOptions {
  /** Message used when the failure carries none of its own. */
  fallbackMessage?: string;
}

/**
 * Build the {@link ApiError} for a GraphQL response's `errors`, carrying both
 * things the server attached to them: the `requestId` (issue #1446) so a toast
 * can quote a support reference, and the `extensions.code` (#3142) so the
 * caller can branch on what the failure *was* rather than on how it reads.
 *
 * Exported because a number of call sites predate {@link runGraphQL} and do
 * their own `if (errors?.length) throw new Error(errors[0].message)`, which
 * discards both. Swapping that line for this one keeps their existing
 * null-`data` handling while restoring the code. New code should use
 * `runGraphQL`, which is stricter about `data`.
 */
export function graphQLErrorsToApiError(
  errors: NonNullable<GraphQLResultLike["errors"]>,
  options: RunGraphQLOptions = {},
): ApiError {
  const first = errors[0];
  const requestId = typeof first?.extensions?.requestId === "string" ? first.extensions.requestId : undefined;
  return new ApiError({
    message: first?.message || options.fallbackMessage || "GraphQL request failed.",
    // Through `readErrorCode` rather than off `first` directly, so an entry
    // without extensions does not mask a later one that has them.
    code: readErrorCode({ errors }),
    requestId,
    originalError: errors,
  });
}

/**
 * Assert a successful Apollo result and return its non-null `data`.
 *
 * @throws {ApiError} if the result carries GraphQL `errors`, or if `data` is
 *   `null`/`undefined`.
 */
export function runGraphQL<R extends GraphQLResultLike>(
  result: R,
  options: RunGraphQLOptions = {},
): NonNullable<R["data"]> {
  const { data, errors } = result;

  if (errors && errors.length > 0) {
    throw graphQLErrorsToApiError(errors, options);
  }

  if (data == null) {
    throw new ApiError({ message: options.fallbackMessage || "GraphQL response contained no data." });
  }

  return data as NonNullable<R["data"]>;
}
